import { Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { LocalStorageService } from 'src/app/@shared/services/local-storage/local-storage.service';
import { eFavoriteType } from '../../enums/favorite/favorite-type.enum';
import { Favorite } from '../../interfaces/favorites/favorite.interface';
import { StoredFavorites } from '../../interfaces/favorites/stored-favorites.interface';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  private static readonly FAVORITE_STORAGE_KEY = 'favorites';

  private _favorites: StoredFavorites;
  private _favoritesStorageKeys: {
    type: eFavoriteType;
    key: string;
    isArray: boolean;
  }[] = [
    {
      type: eFavoriteType.ROUTES,
      key: eFavoriteType.ROUTES,
      isArray: true,
    },
    {
      type: eFavoriteType.ADDRESSES,
      key: eFavoriteType.ADDRESSES,
      isArray: false,
    },
    {
      type: eFavoriteType.LINES,
      key: eFavoriteType.LINES,
      isArray: false,
    },
    {
      type: eFavoriteType.STATIONS,
      key: eFavoriteType.STATIONS,
      isArray: false,
    },
    {
      type: eFavoriteType.HOME_ADDRESS,
      key: 'addresses.homeAddress',
      isArray: false,
    },
    {
      type: eFavoriteType.WORK_ADDRESS,
      key: 'addresses.workAddress',
      isArray: false,
    },
  ];

  onFavoritesChange$ = new ReplaySubject<StoredFavorites>();

  constructor(private readonly localStorageService: LocalStorageService) {
    this.favorites = this.localStorageService.getNestedItem<{
      favorites: StoredFavorites;
    }>(FavoritesService.FAVORITE_STORAGE_KEY)?.favorites;
  }

  addNewFavorite(favorite: Favorite) {
    if (!favorite.id) favorite.id = uuidv4();

    const storageKey = this._favoritesStorageKeys.find(
      (storageKey) => storageKey.type == favorite.type
    );

    if (!this._favorites || !this._favorites[storageKey.key]) {
      this._favorites = this.updateNestedFavorite(
        this._favorites,
        storageKey.key,
        storageKey.isArray ? [] : null
      );
    }

    let storedFavorites = this._favorites[storageKey.key];

    if (Array.isArray(storedFavorites)) {
      storedFavorites.push(favorite);
    } else {
      storedFavorites = favorite;
    }

    this.setNestedFavoriteInStorage(storageKey.key, storedFavorites);
    this.favorites = this.updateNestedFavorite(
      this._favorites,
      storageKey.key,
      storedFavorites
    );
  }

  updateFavorite(favorite: Favorite) {
    const storageKey = this._favoritesStorageKeys.find(
      (storageKey) => storageKey.type == favorite.type
    );

    if (!this._favorites || !this._favorites[storageKey.key]) {
      this.addNewFavorite(favorite);
      return;
    }

    let storedFavorites = this._favorites[storageKey.key];
    if (Array.isArray(storedFavorites)) {
      const favoriteIndex = storedFavorites.findIndex(
        (fav) => fav.id == favorite.id
      );

      if (favoriteIndex !== -1) storedFavorites[favoriteIndex] = favorite;
    } else {
      storedFavorites = favorite;
    }

    this.setNestedFavoriteInStorage(storageKey.key, storedFavorites);
    this.favorites = this.updateNestedFavorite(
      this._favorites,
      storageKey.key,
      storedFavorites
    );
  }

  removeFavorite(favorite: Favorite) {
    const storageKey = this._favoritesStorageKeys.find(
      (storageKey) => storageKey.type == favorite.type
    );

    if (!this._favorites || !this._favorites[storageKey.key]) return;

    let storedFavorites = this._favorites[storageKey.key];

    if (Array.isArray(storedFavorites)) {
      const favoriteIndex = storedFavorites.findIndex(
        (fav) => fav.id == favorite.id
      );

      storedFavorites.splice(favoriteIndex, 1);
    } else {
      storedFavorites = favorite;
    }

    this.localStorageService.setNestedItem(
      `${FavoritesService.FAVORITE_STORAGE_KEY}.${storageKey.key}`,
      storedFavorites
    );
    this.favorites[storageKey.key] = storedFavorites;
  }

  private set favorites(favorites: StoredFavorites) {
    this._favorites = favorites;
    this.onFavoritesChange$.next(this._favorites);
  }

  get favorites(): StoredFavorites {
    return this._favorites;
  }

  private setNestedFavoriteInStorage(
    key: string,
    storedFavorites: Favorite | Favorite[]
  ) {
    this.localStorageService.setNestedItem(
      `${FavoritesService.FAVORITE_STORAGE_KEY}.${key}`,
      storedFavorites
    );
  }

  private updateNestedFavorite(
    obj: any,
    path: string,
    newValue: Favorite | Favorite[]
  ): any {
    let objPath = path.split('.');

    if (!obj) {
      obj = {};
    }

    let current = obj;

    for (let i = 0; i < objPath.length - 1; i++) {
      if (!current[objPath[i]]) {
        current[objPath[i]] = {};
      }
      current = current[objPath[i]];
    }

    current[objPath[objPath.length - 1]] = newValue;

    return obj;
  }
}
